import { useEffect } from 'react';

/**
 * ГОРЯЧИЕ КЛАВИШИ КОНСОЛИ. Решение владельца 2026-09-26 (6).
 *
 * Находка аудита единицы 16 (29): горячих клавиш не было НИ ОДНОЙ, при
 * том что 48 падов пронумерованы 1…48 - номер обещает клавишу, которой не
 * существует. Инструмент эфира без клавиатуры - инструмент для второй
 * руки, которой у стримера нет: одна на мыши, вторая нужна здесь.
 *
 * ЧТО НАЗНАЧЕНО: пады 1-9, «Стоп» (обрывает звук в эфире) и «Пауза».
 * Больше девяти падов клавишами не берётся: десятый потребовал бы
 * двузначного ввода, то есть ожидания второй клавиши, - а «Стоп» обязан
 * срабатывать мгновенно.
 *
 * КОД, А НЕ СИМВОЛ. `event.code` - физическая клавиша, `event.key` -
 * символ раскладки. На русской раскладке `key` для клавиши S даёт «ы», и
 * проверка по символу молчала бы ровно у той аудитории, ради которой
 * продукт сделан.
 *
 * КОГДА НЕ РАБОТАЮТ:
 *   - фокус в поле ввода, в области редактирования или в списке - там
 *     цифра это цифра, а буква это буква;
 *   - открыто окно: у окна свой контекст и своё главное действие;
 *   - нажат модификатор - сочетания принадлежат браузеру и системе, и
 *     перехватывать их значит ломать то, чем человек пользуется всегда.
 */

/** Клавиша → что нажать. Пометки те же, по которым ищут сценарии. */
const KEYS: Record<string, string> = {
  KeyS: '[data-topbar="sound-panic"]',
  KeyP: '[data-left="ctrl-pause"]',
};

/**
 * Органы с клавишей - для ворот «клавиша в подсказке и в имени для чтеца, а
 * не в подписи» (`e2e/console-hotkeys.spec.ts`, единица 21, N-09). Список
 * ведётся здесь же, рядом с привязкой: новая клавиша попадает в ворота сама.
 */
export const HOTKEY_TARGETS: { key: string; selector: string }[] = [
  ...Object.entries(KEYS).map(([code, selector]) => ({ key: code.replace('Key', ''), selector })),
  { key: 'digit', selector: '[data-pad-index]' },
];

/** Подписи для подсказок и гида: одно место на весь продукт. */
export const HOTKEYS = {
  pads: '1…9',
  panic: 'S',
  pause: 'P',
} as const;

/** Набирает ли человек текст прямо сейчас. */
function typing(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;

  const tag = target.tagName.toLowerCase();

  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (target.isContentEditable) return true;

  /* Список, набор вариантов и меню разбирают цифры и буквы сами:
     Radix ищет по первой букве, стрелки двигают выбор. */
  return target.closest('[role="listbox"],[role="menu"],[role="combobox"]') !== null;
}

export function useConsoleHotkeys(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.repeat) return;
      if (typing(event.target)) return;
      /* Открытое окно - свой контекст: пады за ним не видны, и нажатие
         цифры в окне тарифа не должно отправлять звук в эфир. */
      if (document.querySelector('[role="dialog"]') !== null) return;

      const digit = /^Digit([1-9])$/.exec(event.code);
      const selector = digit === null ? KEYS[event.code] : `[data-pad-index="${String(digit[1])}"]`;

      if (selector === undefined) return;

      const target = document.querySelector(selector);

      if (!(target instanceof HTMLElement)) return;
      /* Кнопки нет на экране или она недоступна - клавиша тоже ничего не
         делает: горячая клавиша повторяет нажатие, а не обходит правила. */
      if (target.hasAttribute('disabled')) return;

      event.preventDefault();
      target.click();
    };

    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [enabled]);
}
